import _ from "lodash"
import * as React from "react"
import { useTranslation } from "react-i18next"

import useEditor from "shared/hooks/useEditor"
import { IOptionList } from "shared/hubrise/models"
import { findUsages } from "shared/hubrise/utils/OptionList"
import * as Sku from "shared/hubrise/utils/Sku"

const useOptionListUsages = (outputType: "short" | "long"): ((optionList: IOptionList) => string) => {
  const { t } = useTranslation()
  const { catalog } = useEditor()

  const usageFn = (optionList: IOptionList): string => {
    const usages = findUsages(catalog, optionList)

    const isShort = outputType === "short"
    if (usages.length === 0) {
      return isShort ? t("option_list.usages.short.not_used") : t("option_list.usages.long.not_used")
    } else if (usages.length === 1) {
      const { product, sku } = usages[0]
      const variables = {
        sku_name: Sku.fullName(product, sku),
      }
      return isShort
        ? t("option_list.usages.short.used_in_one_sku", variables)
        : t("option_list.usages.long.used_in_one_sku", variables)
    } else if (_.uniqBy(usages, (usage) => usage.product).length === 1) {
      const product = usages[0].product
      const variables = {
        product_name: product.name,
        nb_skus: usages.length,
      }
      return isShort
        ? t("option_list.usages.short.used_in_one_product", variables)
        : t("option_list.usages.long.used_in_one_product", variables)
    } else {
      const variables = { nb_skus: usages.length }
      return isShort
        ? t("option_list.usages.short.used_in_several_skus", variables)
        : t("option_list.usages.long.used_in_several_skus", variables)
    }
  }

  return React.useCallback(usageFn, [outputType, t, catalog])
}

export default useOptionListUsages
